import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import request from '../../../utils/Request.utils';

const IMAGE_FILE_PATTERN = /\.(png|jpe?g|gif)$/i;
const getAttachmentFile = (attachment) => attachment?.att_file_seq_al_upload_file;
const getFileViewerUrl = (fileSeq) => `${process.env.REACT_APP_API_URL}/upload/viewer/${fileSeq}`;
const isImageFile = (file) => IMAGE_FILE_PATTERN.test(file?.file_real_name || file?.file_sys_name || '');
const formatFileSize = (fileSize) => `${((Number(fileSize) || 0) / 1024).toFixed(2)} KB`;
const INQUIRY_ANSWER_MAX_LENGTH = 1000;
const limitInquiryAnswer = (value) => value.slice(0, INQUIRY_ANSWER_MAX_LENGTH);
const getInquiryAnswerCount = (value) => `${value.length.toLocaleString()} / ${INQUIRY_ANSWER_MAX_LENGTH.toLocaleString()}자`;
const formatInquiryDateTime = (dateValue) => {
  if (!dateValue) return "YYYY-MM-DD";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;

  const parts = new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

const InquiryWrite = () => {
  const navigate = useNavigate();
  const { board_seq } = useParams();

  const [inquiryDate, setInquiryDate] = useState("");
  const [id, setId] = useState("");
  // const [nickname, setNickname] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [answer, setAnswer] = useState("");
  const [initialAnswer, setInitialAnswer] = useState("");
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    if (board_seq) {
      const fetchInquiryData = async () => {
        try {
          const successHandler = (response) => {
            const data = response.data;
            setInquiryDate(data.reg_dtm);
            setId(data.owner_seq_al_user?.user_id || "");
            // setNickname(data.user_nickname || "");
            setTitle(data.board_title);
            setContent(data.board_content);
            const savedAnswer = data.board_content2 || "";
            setAnswer(savedAnswer);
            setInitialAnswer(savedAnswer);
            setAttachments(data.al_board_atts || []);
          };

          const errorHandler = (error) => {
            toast.error("문의 불러오기 중 오류가 발생했습니다.");
          };

          await request.get(`/board/list/admin/BD_INQUIRY/${board_seq}`, {}, successHandler, errorHandler);
        } catch (error) {
          toast.error("문의 불러오기 중 오류가 발생했습니다.");
        }
      };
      fetchInquiryData();
    }
  }, [board_seq]);

  // const handleTitleChange = (e) => setTitle(e.target.value);
  // const handleContentChange = (e) => setContent(e.target.value);
  const handleAnswerChange = (e) => setAnswer(limitInquiryAnswer(e.target.value));

  const handleFileDownload = (fileSeq) => {
    window.open(`${process.env.REACT_APP_API_URL}/upload/download/${fileSeq}`, '_blank');
  };

  const renderAttachments = () => {
    const files = attachments.map(getAttachmentFile).filter(Boolean);
    const imageFiles = files.filter(isImageFile);
    const downloadFiles = files.filter(file => !isImageFile(file));

    if (files.length === 0) return null;

    return (
      <div style={{ marginTop: '12px' }}>
        {imageFiles.map(file => (
          <img
            key={file.up_file_seq}
            src={getFileViewerUrl(file.up_file_seq)}
            alt={file.file_real_name || '첨부 이미지'}
            style={{ display: 'block', maxWidth: '320px', maxHeight: '220px', marginTop: '8px' }}
          />
        ))}
        {downloadFiles.length > 0 && (
          <ul className="attach_file_list">
            {downloadFiles.map(file => (
              <li key={file.up_file_seq}>
                <button
                  type="button"
                  className="adm_com_btn gray line"
                  onClick={() => handleFileDownload(file.up_file_seq)}
                >
                  {file.file_real_name} ({formatFileSize(file.file_size)})
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const handleSubmit = async () => {
    if (answer.trim() === "") {
      toast.error("답변 내용을 입력해 주세요.");
      return;
    }

    if (answer.length > INQUIRY_ANSWER_MAX_LENGTH) {
      toast.error(`답변은 ${INQUIRY_ANSWER_MAX_LENGTH.toLocaleString()}자 이하로 입력해 주세요.`);
      return;
    }

    if (board_seq && answer === initialAnswer) {
      toast.error("수정된 내용이 없습니다.");
      return;
    }

    const formData = {
      // board_title: title,
      // board_content: content,
      board_content2: answer
    };

    try {
      const successHandler = (response) => {
        console.log('문의 답변등록 response', response)
        if (response.messageCode === 200) {
          toast.success(`문의가 성공적으로 ${board_seq ? "수정" : "등록"}되었습니다.`);
          navigate("/Manage/Inquiry");
        } else if (response.messageCode === 411) {
          toast.error("수정된 내용이 없습니다.");
        } else {
          toast.error(`문의 ${board_seq ? "수정" : "등록"} 중 오류가 발생했습니다.`);
        }
      };

      const errorHandler = (error) => {
        toast.error(`문의 ${board_seq ? "수정" : "등록"} 중 오류가 발생했습니다.`);
      };

      if (board_seq) {
        await request.put(`/board/BD_INQUIRY/${board_seq}`, formData, successHandler, errorHandler);
      } else {
        await request.post("/board/BD_INQUIRY", formData, successHandler, errorHandler);
      }

    } catch (error) {
      toast.error(`문의 ${board_seq ? "수정" : "등록"} 중 오류가 발생했습니다.`);
    }
  };

  return (
    <main>
      <h2 className="adm_com_pg_tit">문의 {board_seq ? "수정" : "등록"}</h2>
      <div className="adm_com_pg_content">
        <table className='adm_com_board_list type_row'>
          <colgroup>
            <col width="10%" /><col />
            <col width="10%" /><col />
          </colgroup>
          <tbody>
            <tr>
              <th>등록일</th>
              <td colSpan={3}>{formatInquiryDateTime(inquiryDate)}</td>
            </tr>
            <tr>
              <th>작성자</th>
              <td>
                {id}
              </td>
              {/* <th>닉네임</th>
              <td>
                {nickname}
              </td> */}
            </tr>
            <tr>
              <th>제목</th>
              <td colSpan={3} className="board_detail_title">{title}</td>
            </tr>
            <tr>
              <th>문의내용</th>
              <td colSpan={3} className="board_detail_content">
                <div className="board_detail_text">{content}</div>
                {renderAttachments()}
              </td>
            </tr>
            <tr>
              <th>답변</th>
              <td colSpan={3}>
                <textarea
                  className="textarea"
                  value={answer}
                  onChange={handleAnswerChange}
                  maxLength={INQUIRY_ANSWER_MAX_LENGTH}
                ></textarea>
                <p className="adm_char_count">{getInquiryAnswerCount(answer)}</p>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="adm_com_btn_wrap center">
          <button className="adm_com_btn blue" onClick={handleSubmit}>{board_seq ? "수정" : "등록"}</button>
          <button className="adm_com_btn gray" onClick={() => navigate("/Manage/Inquiry")}>취소</button>
        </div>
      </div>
      <ToastContainer />
    </main>
  );
};

export default InquiryWrite;
